/**
 * PlaybookTemplate — presentational body for a CIH V3 Playbook detail page
 * (/coaching-business/playbooks/[slug]).
 *
 * Pure render component: takes a published playbook row plus its joined thought
 * leader, annotated Tiptap body + heading anchors and renders the hero, the
 * Person byline (the thought leader who originated the framework), the article
 * body (the four mandated sections live inside the Tiptap `body`, so
 * `ArticleBody` renders them), an optional pull quote, the Yuri reviewer byline,
 * the FAQ section, and the Accelerator-oriented Coach Business Builder CTA.
 *
 * The thought leader is presented in the third person as the framework's
 * originator — never as the author or as endorsing DCM (playbook IP protocol).
 *
 * Kept free of loaders/router data so it is render-testable in isolation — the
 * route component calls it with `Route.useLoaderData()`.
 *
 * Aliases: playbook template, playbook detail body, cbb playbook page,
 * person byline
 */
import { ArticleBody } from "~/components/cih/article-body";
import { CbbCta } from "~/components/cih/cbb-cta";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihHero } from "~/components/cih/hero";
import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { CihReviewerByline } from "~/components/cih/reviewer-byline";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { SeriesNav } from "~/components/cih/series-nav";
import type { GuideRow } from "~/lib/server/cih/guides";
import type {
  PlaybookRow,
  PlaybookWithRelations,
} from "~/lib/server/cih/playbooks";
import type { BodyHeading } from "~/lib/shared/cih/body-toc";
import type { SeriesNavResult } from "~/lib/shared/cih/library-series";
import type { JsonValue } from "~/lib/shared/json";

type ThoughtLeader = PlaybookWithRelations["thoughtLeader"];

/** A series can mix guides and playbooks — see `library-series.ts`. */
type PlaybookSeriesNav = SeriesNavResult<
  GuideRow & { slug: string },
  PlaybookRow & { slug: string }
>;

type Props = {
  readonly playbook: PlaybookRow;
  /** The attributed thought leader, joined for the Person byline. */
  readonly thoughtLeader: ThoughtLeader;
  /** Tiptap doc with stable heading `id` attrs (from annotateBodyHeadings). */
  readonly bodyDoc: JsonValue | null;
  /** Ordered H2 anchors (unused for nav today; kept for parity + future use). */
  readonly headings: BodyHeading[];
  /** Previous/next series nav, or null when the playbook belongs to no series. */
  readonly seriesNav?: PlaybookSeriesNav | null;
};

export function PlaybookTemplate({
  playbook,
  thoughtLeader,
  bodyDoc,
  headings: _headings,
  seriesNav,
}: Props) {
  const faqs = playbook.faqs ?? [];

  return (
    <>
      <CihHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Coaching Business", href: "/coaching-business" },
          { label: "Playbooks", href: "/coaching-business/playbooks" },
          {
            label: playbook.title,
            href: playbook.slug
              ? `/coaching-business/playbooks/${playbook.slug}`
              : "/coaching-business/playbooks",
          },
        ]}
        kicker="Playbook"
        kickerColorClass="text-green-700"
        title={playbook.title}
        lede={playbook.description}
      />

      {thoughtLeader ? (
        <div className="border-b border-gray-200">
          <div className="mx-auto max-w-7xl px-4 py-5 xl:px-0">
            <p className="text-sm text-gray-600">
              Framework originated by{" "}
              <span className="font-medium text-gray-900">
                {thoughtLeader.name}
              </span>
              {thoughtLeader.organization ? (
                <span className="text-gray-400">
                  {" · "}
                  {thoughtLeader.organization}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}

      <CihReviewerByline
        reviewerName={playbook.reviewerName}
        reviewerCredentials={playbook.reviewerCredentials}
        reviewedDate={playbook.reviewedDate}
      />

      {bodyDoc ? (
        <CihSection id="overview" divider={false}>
          <CihSectionHeader label="Playbook" title="Overview" />
          <div className="mx-auto max-w-7xl px-4 xl:px-0">
            <ArticleBody
              body={bodyDoc}
              className="prose prose-lg prose-a:text-green-700"
            />
          </div>
        </CihSection>
      ) : null}

      {playbook.pullQuote ? (
        <CihPullQuoteBand
          quote={playbook.pullQuote}
          attribution={playbook.pullQuoteAttribution}
        />
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection
            faqs={faqs}
            heading={`Questions about ${playbook.title}`}
          />
        </CihSection>
      ) : null}

      {seriesNav ? <SeriesNav nav={seriesNav} /> : null}

      <CbbCta band={playbook.ctaStageBand} kicker="Coach Business Builder" />
    </>
  );
}
